import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { 
  FileText, 
  Image, 
  Trash2, 
  Copy, 
  Eye, 
  ArrowUp, 
  ArrowDown, 
  CheckCircle, 
  Edit, 
  Upload,
  Sparkles,
  FolderX
} from 'lucide-react';
import { markdownToHtml, htmlToMarkdown } from '../utils/markdownConverter';
import { useAuth } from '../context/ApiKeyContext';
import { getApiBaseUrl } from '../config';

const API_BASE_URL = getApiBaseUrl();

/** Botão "ver referências": flag da API ou metadados de referência (ex.: revisão somente leitura). */
function itemShowsReferencias(item) {
  if (!item) return false;
  if (item.possui_referencias) return true;
  if (item.type === "image") return true;
  const m = item.metadata || {};
  if (m.token != null && String(m.token).length > 0) {
    if (m.pagina != null || m.coordenadas != null) return true;
  }
  if (m.id_documento != null && m.pagina != null) return true;
  return false;
}

/**
 * Componente que representa um item individual do documento
 * Pode ser texto ou imagem, com funcionalidades de edição, movimentação e revisão
 */
const DocumentItem = ({
  item,
  itemIndex,
  totalItems,
  isSelected,
  isEditing,
  showDeleteConfirm,
  showDividers,
  onSelect,
  onEdit,
  onContentChange,
  onCancelEdit,
  onSaveEdit,
  onToggleReview,
  isSelectedForReview = false, // Indica se o item está selecionado para revisão múltipla
  multiSelectMode = false, // Indica se o modo de seleção múltipla está ativo
  onViewSources,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onDelete,
  onConfirmDelete,
  onCancelDelete,
  onInsertAbove,
  onInsertBelow,
  onImageUpload,
  uploadingImage = false, // Estado de loading do upload de imagem
  serviceName = 'DOCUMENT_CREATION', // Nome do serviço padrão
  editavel = true, // Define se o documento é editável
  savingEdit = false, // Estado de loading do salvamento
  saveError = null, // Mensagem de erro do salvamento
  // Props para exclusão de seção
  hasChildrenItems = false, // Indica se o item tem filhos (baseado em hierarquia)
  onDeleteSection = null, // Callback para excluir seção inteira
  isMarkedForSectionDelete = false, // Indica se o item está marcado para exclusão de seção
  // Atalhos de fluxo de edição (Abordagem A — editor fluido)
  onCreateBelow = null, // Enter em linha vazia
  onDeleteAndFocusPrev = null, // Backspace em item vazio
  onNavigatePrev = null, // ArrowUp na primeira linha
  onNavigateNext = null, // ArrowDown na última linha
  registerQuill = null, // Registro do Quill instance no parent (focus management)
  fluidMode = false, // Modo "Editar tudo" ativo no editor pai (oculta botões Save/Cancel e toolbar não-focada via CSS)
}) => {
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const { token } = useAuth();

  // Estado local para armazenar o conteúdo HTML durante a edição
  const [editorContent, setEditorContent] = useState('');
  // Estado local para armazenar o conteúdo Markdown durante a edição
  const [localMarkdownContent, setLocalMarkdownContent] = useState('');
  // Conteúdo do último save bem-sucedido (ou do conteúdo inicial). Usado para evitar saves duplicados (blur após Save explícito).
  const lastSavedContentRef = useRef('');
  // Refs estáveis para callbacks/itemId usados dentro de keyboard bindings do Quill
  // (que são instanciados uma vez no mount).
  const fluidCallbacksRef = useRef({});
  fluidCallbacksRef.current = {
    onCreateBelow,
    onDeleteAndFocusPrev,
    onNavigatePrev,
    onNavigateNext,
    itemId: item.id,
  };
  
  // Estados para gerenciar o download da imagem via API
  const [downloadedImageUrl, setDownloadedImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [imageError, setImageError] = useState(null);
  
  // Quando entrar em modo de edição, converte Markdown para HTML e armazena conteúdo original
  useEffect(() => {
    if (isEditing && item.type === 'text') {
      const htmlContent = markdownToHtml(item.content || '');
      setEditorContent(htmlContent);
      setLocalMarkdownContent(item.content || '');
      lastSavedContentRef.current = item.content || '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing, item.type, item.id]); // item.content removido propositalmente: evita resetar cursor durante edição

  // Registra/desregistra a instância do Quill no parent para que ele possa focar
  // este item em fluxos vindos de outro item (Enter cria abaixo, setas navegam, etc).
  useEffect(() => {
    if (!registerQuill || item.type !== 'text') return undefined;
    if (isEditing && quillRef.current) {
      const editor = quillRef.current.getEditor && quillRef.current.getEditor();
      if (editor) {
        registerQuill(item.id, editor);
        return () => registerQuill(item.id, null);
      }
    }
    return undefined;
  }, [isEditing, item.id, item.type, registerQuill]);

  // Quando o item for do tipo imagem e tiver token nos metadados, fazer download via API
  useEffect(() => {
    // Verificar se é uma imagem e se tem token nos metadados
    const hasTokenInMetadata = item.type === 'image' && 
                                item.metadata && 
                                item.metadata.token && 
                                item.metadata.token.trim() !== '';

    if (hasTokenInMetadata && token) {
      const fetchImage = async () => {
        try {
          setLoadingImage(true);
          setImageError(null);

          const downloadUrl = `${API_BASE_URL}/files/download?token=${item.metadata.token}&Servicename=${encodeURIComponent(serviceName)}`;
          console.log('🖼️ Baixando imagem com token:', item.metadata.token.substring(0, 20) + '...');

          const response = await fetch(downloadUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'accept': 'application/octet-stream',
            },
          });

          if (!response.ok) {
            throw new Error(`Erro ao carregar imagem: ${response.status} - ${response.statusText}`);
          }

          const blob = await response.blob();
          const imageUrl = window.URL.createObjectURL(blob);
          setDownloadedImageUrl(imageUrl);
          console.log('✅ Imagem carregada com sucesso');

        } catch (error) {
          console.error('❌ Erro ao carregar imagem:', error);
          setImageError(error.message);
        } finally {
          setLoadingImage(false);
        }
      };

      fetchImage();
    } else {
      // Limpar URL anterior quando o item mudar e não houver token
      setDownloadedImageUrl(prevUrl => {
        if (prevUrl) {
          window.URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    }

    // Cleanup: revogar URL do blob ao desmontar
    return () => {
      setDownloadedImageUrl(prevUrl => {
        if (prevUrl) {
          window.URL.revokeObjectURL(prevUrl);
        }
        return null;
      });
    };
  }, [item.type, item.metadata, token, item.id, serviceName]);

  // Configuração dos módulos do Quill — memoizado: o Quill é inicializado uma vez
  // por mount. Os keyboard handlers leem o ref fluidCallbacksRef para sempre acessar
  // os últimos callbacks/itemId mesmo após re-renders.
  const modules = useMemo(() => ({
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      // Lista explícita para garantir que "justified" apareça como opção visível no dropdown
      [{ 'align': ['', 'center', 'right', 'justify'] }],
      ['link'],
      ['clean']
    ],
    keyboard: {
      bindings: {
        // Enter em linha vazia (fora de lista) → cria novo item abaixo.
        // Em listas, deixa o Quill tratar normalmente (exit-list etc).
        fluidEnterEmpty: {
          key: 'Enter',
          empty: true,
          collapsed: true,
          format: { list: false },
          handler() {
            const cb = fluidCallbacksRef.current;
            if (cb.onCreateBelow) {
              cb.onCreateBelow(cb.itemId);
              return false; // consome, não deixa Quill inserir nova linha
            }
            return true;
          },
        },
        // Backspace em item completamente vazio → deleta e foca anterior.
        fluidBackspaceEmpty: {
          key: 'Backspace',
          collapsed: true,
          handler(range) {
            const editor = this.quill;
            if (!editor || !range) return true;
            const textTotal = editor.getText().replace(/\n/g, '').trim();
            if (textTotal.length === 0 && range.index === 0) {
              const cb = fluidCallbacksRef.current;
              if (cb.onDeleteAndFocusPrev) {
                cb.onDeleteAndFocusPrev(cb.itemId);
                return false;
              }
            }
            return true; // deixa Quill tratar normal
          },
        },
        // Navegação por setas entre itens está desativada por enquanto — o comportamento
        // de boundary (detectar primeira/última linha) tem casos limítrofes com listas
        // e linhas com wrap que tornam o uso inconsistente. Setas voltam ao default do
        // Quill (movimento de cursor dentro do item).
        // Para reativar, restaurar os bindings 'Up'/'Down' chamando onNavigatePrev/Next.
      },
    },
  }), []);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'link'
  ];

  // Handler para mudanças no editor - apenas atualiza o estado local
  const handleEditorChange = (html) => {
    setEditorContent(html);
    const markdown = htmlToMarkdown(html);
    setLocalMarkdownContent(markdown);
  };

  // Handler para salvar - envia o conteúdo local para o pai
  const handleSave = () => {
    lastSavedContentRef.current = localMarkdownContent;
    onSaveEdit(item.id, localMarkdownContent);
  };

  // Autosave on blur: se o conteúdo mudou desde o último save bem-sucedido, salva.
  // Evita chamada redundante se o usuário acabou de clicar em "Salvar" (lastSaved já atualizado).
  // Em caso de erro, o conteúdo local permanece e o pai exibe saveError para retry manual.
  const handleQuillBlur = useCallback(() => {
    if (!isEditing) return;
    if (localMarkdownContent === lastSavedContentRef.current) return;
    lastSavedContentRef.current = localMarkdownContent;
    onSaveEdit(item.id, localMarkdownContent);
  }, [isEditing, item.id, localMarkdownContent, onSaveEdit]);

  // Handler para cancelar - limpa o estado local sem atualizar o pai
  const handleCancel = () => {
    setEditorContent('');
    setLocalMarkdownContent('');
    onCancelEdit(item.id);
  };

  // Duplo clique no texto em modo view → entra em edição direto (atalho que coexiste com o botão).
  // Usamos onMouseDown com e.detail === 2 (segundo clique do double-click) em vez de onDoubleClick,
  // porque o browser seleciona a palavra/parágrafo no mousedown — o preventDefault aqui evita
  // que UI fora do texto seja selecionada visualmente.
  const handleTextMouseDown = (e) => {
    if (!editavel || isEditing || item.type !== 'text') return;
    if (e.detail === 2) {
      e.preventDefault();
      e.stopPropagation();
      // Limpa seleção residual antes de abrir o editor
      const sel = window.getSelection && window.getSelection();
      if (sel && sel.removeAllRanges) sel.removeAllRanges();
      onEdit(item.id);
    }
  };

  // Determinar estilos baseado no estado de exclusão de seção
  const getSectionDeleteStyles = () => {
    if (isMarkedForSectionDelete) {
      return {
        backgroundColor: 'rgba(239, 68, 68, 0.15)',
        borderLeft: '4px solid var(--ikaro-status-error)',
        borderRight: '4px solid var(--ikaro-status-error)',
        boxShadow: 'inset 0 0 0 2px rgba(239, 68, 68, 0.3)',
        animation: 'sectionDeletePulse 2s ease-in-out infinite'
      };
    }
    return {};
  };

  const sectionDeleteStyles = getSectionDeleteStyles();

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        // No modo de seleção múltipla
        if (multiSelectMode && !isMarkedForSectionDelete) {
          // Se o item já foi revisado, não fazer nada (nem selecionar, nem expandir)
          if (item.reviewed) {
            return;
          }
          // Se não foi revisado, selecionar para revisão
          onToggleReview(item.id, true);
          return;
        }
        // Comportamento normal (fora do modo de seleção múltipla)
        if (!isMarkedForSectionDelete) {
          onSelect(item.id);
        }
      }}
      className="relative"
      style={{
        // user-select: none aqui evita que double-click em qualquer área do item
        // selecione badges/botões/UI visualmente. O elemento de texto (.doc-item-text-display)
        // sobrescreve com user-select: text. O Quill em edit mode usa contenteditable e
        // gerencia sua própria seleção.
        userSelect: 'none',
        WebkitUserSelect: 'none',
        backgroundColor: isMarkedForSectionDelete
          ? sectionDeleteStyles.backgroundColor
          : (isSelectedForReview
              ? 'var(--ikaro-orange-light)'
              : (isSelected ? 'var(--ikaro-purple-light)' : 'transparent')),
        borderLeft: isMarkedForSectionDelete 
          ? sectionDeleteStyles.borderLeft 
          : (item.reviewed ? '4px solid var(--ikaro-status-success)' : (isSelected ? '4px solid var(--ikaro-purple-accent)' : '4px solid transparent')),
        borderRight: isMarkedForSectionDelete 
          ? sectionDeleteStyles.borderRight 
          : (isSelected && !item.reviewed ? '4px solid var(--ikaro-purple-accent)' : '4px solid transparent'),
        borderBottom: (showDividers && itemIndex < totalItems - 1) ? '2px solid rgba(100, 49, 132, 0.2)' : 'none',
        cursor: isMarkedForSectionDelete 
          ? 'default' 
          : (multiSelectMode && item.reviewed ? 'not-allowed' : 'pointer'),
        transition: 'all 0.2s ease',
        boxShadow: isMarkedForSectionDelete 
          ? sectionDeleteStyles.boxShadow 
          : (isSelected ? 'inset 0 0 0 1px var(--ikaro-purple-accent)' : 'none'),
        animation: isMarkedForSectionDelete ? sectionDeleteStyles.animation : 'none'
      }}
      onMouseEnter={(e) => {
        if (!isSelected && !isMarkedForSectionDelete && !isSelectedForReview) {
          // No modo de seleção múltipla, não destacar itens já revisados
          if (multiSelectMode && item.reviewed) {
            return;
          }
          e.currentTarget.style.backgroundColor = 'var(--ikaro-bg-surface-hover)';
        } else if (isSelectedForReview) {
          // Se está selecionado para revisão, aplicar um hover mais escuro
          e.currentTarget.style.backgroundColor = 'rgba(255, 193, 7, 0.3)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected && !isMarkedForSectionDelete && !isSelectedForReview) {
          // No modo de seleção múltipla, não destacar itens já revisados
          if (multiSelectMode && item.reviewed) {
            return;
          }
          e.currentTarget.style.backgroundColor = 'transparent';
        } else if (isSelectedForReview) {
          // Restaurar a cor de seleção quando o mouse sair
          e.currentTarget.style.backgroundColor = 'var(--ikaro-orange-light)';
        }
      }}
    >
      {/* Indicador de item criado por IA - só aparece quando editável */}
      {item.indicador_ia && (item.indicador_ia === 1 || item.indicador_ia === 2) && (
        <div
          className="doc-item-ia-custom-badge"
          style={{
            backgroundColor: item.reviewed 
              ? 'var(--ikaro-bg-surface-secondary)' 
              : 'var(--ikaro-bg-surface-secondary)',
            color: item.reviewed 
              ? 'var(--ikaro-gray-medium)' 
              : (item.indicador_ia === 2 ? '#FF9500' : '#FFD60A'),
            boxShadow: item.reviewed 
              ? '0 2px 4px rgba(0,0,0,0.05)' : '0 2px 4px rgba(0,0,0,0.1)'
          }}
          title={
            item.reviewed
              ? 'Item criado por IA - Já revisado'
              : (item.indicador_ia === 2 
                  ? 'Item criado por IA - Sem referências diretas com dados ou teses' 
                  : 'Item criado por IA - Com referência de dados, sem referências com teses')
          }
          onClick={(e) => e.stopPropagation()}
        >
          <Sparkles 
            size={12}
            className="pointer-events-none"
          />
        </div>
      )}

      {/* Overlay de confirmação de exclusão */}
      {showDeleteConfirm && (
        <div 
          className="doc-item-delete-overlay"
          style={{ zIndex: 20 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="doc-item-delete-modal" style={{ maxWidth: '400px', margin: '0 16px' }}>
            <h3 className="text-lg font-semibold text-dark mb-8">
              Confirmar Exclusão
            </h3>
            <p className="text-base text-muted mb-16">
              Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.
            </p>
            <div className="flex-row-end gap-12">
              <button
                onClick={onCancelDelete}
                className="ikaro-btn ikaro-btn-sm ikaro-btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={() => onConfirmDelete(item.id)}
                className="ikaro-btn ikaro-btn-sm ikaro-btn-danger"
              >
                Sim, excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conteúdo do item */}
      <div className="doc-item-content">
        {item.type === 'text' ? (
          isEditing ? (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ userSelect: 'text', WebkitUserSelect: 'text' }}
              className={fluidMode ? 'doc-item-fluid-mode' : undefined}
            >
              {/* Editor WYSIWYG com ReactQuill */}
              <div className="mb-8">
                <ReactQuill
                  ref={quillRef}
                  value={editorContent}
                  onChange={handleEditorChange}
                  onBlur={handleQuillBlur}
                  onFocus={() => {
                    // Foco no Quill = item ativo. Reusa o mesmo handler do clique
                    // (handleItemClick no parent) para atualizar selectedItemId e,
                    // se o painel de referências está aberto, recarregar suas refs.
                    // Essencial no modo "Editar tudo": clicar dentro de uma caixa não
                    // propaga para o onClick do container, então sem isso a sidebar não
                    // acompanharia o usuário entre itens.
                    if (onSelect) onSelect(item.id);
                  }}
                  modules={modules}
                  formats={formats}
                  theme="snow"
                  placeholder="Digite o texto aqui..."
                  className="doc-item-editor-wrapper"
                />
              </div>

              {/* No modo "Editar tudo", o autosave (on blur) cuida da persistência.
                  Os botões Salvar/Cancelar só aparecem na edição individual de um item. */}
              {!fluidMode && (
                <div className="flex-col gap-8 mt-16">
                  <div className="flex-row gap-8">
                    <button
                      onClick={handleSave}
                      disabled={savingEdit}
                      className="ikaro-btn ikaro-btn-sm ikaro-btn-primary"
                    >
                      {savingEdit && (
                        <div className="doc-item-save-spinner" />
                      )}
                      {savingEdit ? 'Salvando...' : 'Salvar'}
                    </button>
                    <button
                      onClick={handleCancel}
                      disabled={savingEdit}
                      className="ikaro-btn ikaro-btn-sm ikaro-btn-secondary"
                    >
                      Cancelar
                    </button>
                  </div>

                  {/* Mensagem de erro */}
                  {saveError && (
                    <div className="ikaro-alert ikaro-alert-error ikaro-alert-bordered" style={{ alignItems: 'flex-start' }}>
                      <span className="flex-shrink-0" style={{ marginTop: '1px' }}>⚠️</span>
                      <span className="flex-1">{saveError}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div
              className="doc-item-text-display ql-editor"
              onMouseDown={handleTextMouseDown}
              title={editavel ? "Clique duas vezes para editar" : undefined}
              style={editavel ? { cursor: 'text', userSelect: 'text' } : { userSelect: 'text' }}
              dangerouslySetInnerHTML={{
                __html: markdownToHtml(item.content || '') || '<p>Clique para editar...</p>'
              }}
            />
          )
        ) : (
          <div className="flex-col gap-12">
            {loadingImage ? (
              // Estado de carregamento da imagem
              <div className="doc-item-image-loading-container">
                <Image className="doc-item-icon-centered" size={48} />
                <p className="text-base text-muted">
                  Carregando imagem...
                </p>
              </div>
            ) : imageError ? (
              // Estado de erro ao carregar imagem
              <div className="doc-item-image-error-container">
                <Image className="doc-item-icon-error" size={48} />
                <p className="font-semibold mb-8" style={{ color: 'var(--ikaro-status-error-text)' }}>
                  Erro ao carregar imagem
                </p>
                <p className="text-sm text-muted">
                  {imageError}
                </p>
              </div>
            ) : (downloadedImageUrl || item.imageUrl) ? (
              // Usar URL baixada via API ou URL direta do item
              <img
                src={downloadedImageUrl || item.imageUrl}
                alt={item.caption || 'Imagem do documento'}
                className="doc-item-image"
              />
            ) : (
              // Nenhuma imagem disponível - mostrar opção de upload
              <div className="doc-item-upload-container">
                {uploadingImage ? (
                  // Mostrar loading durante o upload
                  <>
                    <div className="doc-item-upload-spinner" />
                    <p className="text-base font-medium mb-8" style={{ color: 'var(--ikaro-orange-primary)' }}>
                      Processando imagem...
                    </p>
                    <p className="text-xs text-muted">
                      Convertendo para PDF e adicionando aos documentos de origem
                    </p>
                  </>
                ) : (
                  <>
                    <Image className="doc-item-icon-centered-sm" size={48} />
                    <p className="text-muted mb-12">
                      Nenhuma imagem carregada
                    </p>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                      className="ikaro-btn ikaro-btn-sm ikaro-btn-primary"
                    >
                      <Upload size={16} />
                      Carregar Imagem
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => onImageUpload(item.id, e)}
                      className="hidden"
                    />
                  </>
                )}
              </div>
            )}
            {item.caption && (
              <p className="text-base text-muted italic">
                {item.caption}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Barra de ações quando o item está selecionado */}
      {isSelected && (
        <div className="doc-item-action-bar">
          {/* Se documento não é editável, mostrar apenas referências */}
          {!editavel ? (
            <>
              {/* Visualização de fontes - apenas se tem referências */}
              {itemShowsReferencias(item) && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewSources(item);
                  }}
                  className="doc-item-action-btn"
                  title="Ver referências"
                >
                  <Eye size={18} />
                </button>
              )}
              {!itemShowsReferencias(item) && (
                <span className="text-sm text-muted italic">
                  Documento em modo somente leitura
                </span>
              )}
            </>
          ) : (
            <>
              {/* Grupo 1: Revisão */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Não permitir selecionar itens já revisados no modo de seleção múltipla
                  if (multiSelectMode && item.reviewed) {
                    return;
                  }
                  // No modo de seleção múltipla, sempre usar seleção múltipla
                  const isMultiSelect = multiSelectMode || e.ctrlKey || e.metaKey;
                  onToggleReview(item.id, isMultiSelect);
                }}
                className="doc-item-review-btn"
                style={{
                  color: item.reviewed ? 'var(--ikaro-status-success)' : 'var(--ikaro-gray-dark)',
                  cursor: (multiSelectMode && item.reviewed) ? 'not-allowed' : 'pointer',
                  opacity: (multiSelectMode && item.reviewed) ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSelectedForReview) {
                    e.target.style.backgroundColor = item.reviewed 
                      ? 'var(--ikaro-status-success-bg)' 
                      : 'var(--ikaro-gray-medium)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelectedForReview) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
                title={item.reviewed 
                  ? "Marcar como não revisado (Ctrl+click para seleção múltipla)" 
                  : "Marcar como revisado (Ctrl+click para seleção múltipla)"}
              >
                <CheckCircle size={18} style={{ fill: item.reviewed ? 'currentColor' : 'none' }} />
              </button>
              
              <div className="doc-item-action-divider"></div>

              {/* Grupo 2: Visualização de fontes */}
              {itemShowsReferencias(item) && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewSources(item);
                    }}
                    className="doc-item-action-btn"
                    title="Ver referências"
                  >
                    <Eye size={18} />
                  </button>
                  <div className="doc-item-action-divider"></div>
                </>
              )}

              {/* Grupo 3: Edição */}
              {item.type === 'text' && !isEditing && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(item.id);
                  }}
                  className="doc-item-action-btn"
                  title="Editar texto"
                >
                  <Edit size={18} />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDuplicate(item.id);
                }}
                className="doc-item-action-btn"
                title="Duplicar item"
              >
                <Copy size={18} />
              </button>
              
              <div className="doc-item-action-divider"></div>

              {/* Grupo 4: Mover */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveUp(item.id);
                }}
                disabled={itemIndex === 0}
                className="doc-item-action-btn"
                style={{
                  opacity: itemIndex === 0 ? 0.3 : 1,
                  cursor: itemIndex === 0 ? 'not-allowed' : 'pointer'
                }}
                title="Mover para cima"
              >
                <ArrowUp size={18} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveDown(item.id);
                }}
                disabled={itemIndex === totalItems - 1}
                className="doc-item-action-btn"
                style={{
                  opacity: itemIndex === totalItems - 1 ? 0.3 : 1,
                  cursor: itemIndex === totalItems - 1 ? 'not-allowed' : 'pointer'
                }}
                title="Mover para baixo"
              >
                <ArrowDown size={18} />
              </button>
              
              <div className="doc-item-action-divider"></div>

              {/* Grupo 5: Inserir */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInsertAbove(item.id);
                }}
                className="doc-item-action-btn"
                title="Inserir item acima"
              >
                <div className="relative">
                  <ArrowUp size={18} />
                  <FileText size={10} style={{ position: 'absolute', bottom: '-2px', right: '-2px' }} />
                </div>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInsertBelow(item.id);
                }}
                className="doc-item-action-btn"
                title="Inserir item abaixo"
              >
                <div className="relative">
                  <ArrowDown size={18} />
                  <FileText size={10} style={{ position: 'absolute', bottom: '-2px', right: '-2px' }} />
                </div>
              </button>

              {/* Grupo 6: Excluir (à direita) */}
              <div className="doc-item-action-divider ml-auto"></div>
              
              {/* Botão de excluir seção - só aparece em itens pai que têm filhos */}
              {hasChildrenItems && onDeleteSection && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteSection(item.id);
                  }}
                  className="doc-item-section-delete-btn"
                  title="Excluir toda a seção (este item e seus sub-itens)"
                >
                  <FolderX size={16} />
                  <span>Excluir Seção</span>
                </button>
              )}
              
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item.id);
                }}
                className="doc-item-delete-action-btn"
                title="Excluir item"
              >
                <Trash2 size={18} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentItem;
